import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_0f2dc054-d2bc-44be-ab56-5351417afe21/artifacts/a2cocwjb_3B8AECB1-0E02-4890-89FD-1DB815461399.png';

export default function Register() {
  const { register } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      await register(email.trim(), password, marketingOptIn);
      toast.success('Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img 
              src={LOGO_URL} 
              alt="In The Stands" 
              className="w-64 h-auto mx-auto mb-4 cursor-pointer hover:opacity-80 transition-opacity"
            />
          </Link>
          <h1 className="text-2xl font-bold text-white mb-2">Create Account</h1>
          <p className="text-gray-400">Start tracking stats today</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
            <input
              type="email"
              data-testid="register-email-input"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#1A1A1A] text-white rounded-xl py-4 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
            <input
              type={showPassword ? 'text' : 'password'}
              data-testid="register-password-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#1A1A1A] text-white rounded-xl py-4 pl-12 pr-12 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
            <input
              type={showPassword ? 'text' : 'password'}
              data-testid="register-confirm-password-input"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full bg-[#1A1A1A] text-white rounded-xl py-4 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
            />
          </div>

          {/* Marketing Opt-In Checkbox */}
          <div className="flex items-start gap-3 py-2">
            <button
              type="button"
              data-testid="marketing-opt-in-checkbox"
              onClick={() => setMarketingOptIn(!marketingOptIn)}
              className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                marketingOptIn 
                  ? 'bg-[#00E5E5] border-[#00E5E5]' 
                  : 'border-gray-500 bg-transparent'
              }`}
            >
              {marketingOptIn && <Check className="w-4 h-4 text-black" />}
            </button>
            <label 
              className="text-sm text-gray-400 cursor-pointer"
              onClick={() => setMarketingOptIn(!marketingOptIn)}
            >
              Yes, I'd like to receive updates, special offers, and tips to get the most out of In The Stands. You can unsubscribe at any time.
            </label>
          </div>

          <button
            type="submit"
            data-testid="register-submit-btn"
            disabled={isLoading}
            className="w-full bg-[#00E5E5] text-black font-bold py-4 rounded-xl hover:bg-[#5FFBFB] transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              'CREATE ACCOUNT'
            )}
          </button>
        </form>

        <p className="text-center mt-6 text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="text-[#00E5E5] font-semibold hover:underline">
            Sign In
          </Link>
        </p>

        <p className="text-center mt-4 text-xs text-gray-500">
          Start with 2 free game entries. Subscribe for unlimited tracking.
        </p>
      </div>
    </div>
  );
}
